import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { delay, filter, first } from 'rxjs/operators';

import { BaseClass } from 'src/app/shared/base.class';
import { IChatMessage, IChatResponse, IChatEvent, IChatUser } from './chat.interfaces';
import { fileToChatMessages } from './chat.utilities';
import { formatChatMessageTime } from 'src/app/shared/utilities/formatting.utilities';
import { UsersService } from '../data/users/users.service';
import { MapObject } from 'src/app/shared/utilities/types.utilities';
import { findLast } from 'src/app/shared/utilities/general.utilities';
import { User } from '../data/users/user.class';
import { Space } from '../data/spaces/space.class';

import * as dayjs from 'dayjs';

@Injectable({
    providedIn: 'root',
})
export class ChatService extends BaseClass {
    /** Mapping of chat conversations to messages */
    private _conversation = new BehaviorSubject<IChatMessage[]>([]);
    /** Websocket listener */
    private _socket: WebSocketSubject<IChatResponse>;
    /** Websocket listener */
    private _files: MapObject<IChatResponse[]> = {};
    /** Event subject for chat */
    private _events = new Subject<IChatEvent>();
    /** ID of the active meeting */
    private _meeting_id: string;
    /** Participants in the chat call */
    private _participants = new BehaviorSubject([]);
    /** Only person in the room */
    private _is_alone: boolean = true;
    /** Role of the active user */
    private _role: string = 'patient';

    /** Fake chat bot user */
    readonly chat_bot = new User(this._users, { id: 'chat-bot', name: 'Chat Bot' });

    constructor(private _users: UsersService) {
        super();
        this.interval('display_time', () => this.updateDisplayTimes(), 10 * 1000);
        this.interval('ping', () => this.ping(), 25 * 1000);
    }

    /** Observable for chat messages */
    public get messages(): Observable<IChatMessage[]> {
        return this._conversation.asObservable();
    }

    /** Observable for chat events */
    public get events(): Observable<IChatEvent> {
        return this._events.asObservable();
    }

    /** Observable for participants */
    public get participants(): Observable<any[]> {
        return this._participants.asObservable();
    }

    public setRole(type: string) {
        this._role = type; 
    }

    /**
     * Initialise websocket chat session
     */
    public start(meeting: User, space: Space): void {
        this._meeting_id = meeting.pexip_id;

        if (!this._socket) {
            const protocol = `ws${location.protocol.indexOf('https') >= 0 ? 's' : ''}:`;
            this._socket = webSocket(`${protocol}//${location.host}/api/chat`);
            this.timeout('load', () => this.load(), 20);
        }

        this.subscription(
            'chat.socket',
            this._socket.subscribe(
                (message) => {
                    if (!this._meeting_id) {
                        this.timeout('load', () => this.load(), 20);
                    }

                    const user_name = this._users.current.name.trim();

                    if (message.meeting_id !== undefined && message.name !== user_name) {
                        if (message.file_name) {
                            this.handleFile(message);
                        } else if (!message.text && message.meeting_id) {
                            if (this._meeting_id !== message.meeting_id) {
                                this._meeting_id = message.meeting_id;
                                this._events.next({ type: 'transfer', message });
                            }
                            if (message.participants) {
                                this._participants.next(message.participants);
                            }
                        } else if (message.meeting_id === null) {
                            this._events.next({ type: 'end' });
                        } else {
                            this._meeting_id = message.meeting_id;
                            this.handleMessage(message);
                        }

                        this.timeout('save', () => this.save(), 30);
                    }
                },
                (_e) => this._events.next({ type: 'end' })
            )
        );

        this.subscription(
            'chat.events',
            this._events
                .pipe(
                    filter((event) => event.type === 'file'),
                    delay(500)
                )
                .subscribe(() => {
                    this.add(space.chatbotFileUploadMessage());
                })
        );

        // This works for the moment to get around the clinicians role in
        // _participants not being set correctly.
        // this._participants.subscribe
        this.subscription(
            'chat.participants',
            this._socket.pipe(first(({ role }) => role === 'clinician')).subscribe(() => {
                this._is_alone = false;
            })
        );
        if (this._role !== 'patient') return;
        this.timeout(
            'chat.init',
            () => {
                this.chatbotSystem(space, space.chatbotSystemMessages('patient'));
                this.chatbotStandard(space, space.chatbotStandardMessages('patient'));
            },
            1000
        );
    }

    /**
     * End the current chat session
     */
    public end() {
        this._participants.next([]);
        this._conversation.next([]);

        if (this._socket) {
            this._socket.complete();
            this._socket = null;
        }

        this.clear();
    }

    /** Update participants */
    public updateParticipants(participants: IChatUser[]): void {
        // TO DO: send participants message
        this._participants.next(participants);
    }

    /**
     * Clear any stored messages
     * @param meeting_id ID of the meeting to be cleared
     */
    public clear(meeting_id?: string): void {
        if (!sessionStorage) return;

        if (meeting_id) {
            this.removeSession(this.messages_meeting);
            this.removeSession(this.messages_stored);
        } else {
            Object.keys(sessionStorage)
                .filter((key) => /MVC_NSW_HP.messages/.test(key))
                .forEach((key) => sessionStorage.removeItem(key));
        }
    }

    /**
     * Send data to chat
     * @param message IChatMessage | File
     */
    public async send(message: Partial<IChatMessage> | File): Promise<void> {
        if (!this._socket || !message?.text) return;

        const meeting_id = this._meeting_id;
        const from = this._users.current.id;
        const name = this._users.current.name;

        if (message instanceof File) {
            const new_messages = await fileToChatMessages(meeting_id, message as File);

            for (const new_message of new_messages) {
                this.handleFile({ ...new_message, from, name } as any);
                this._socket.next({ ...new_message, meeting_id } as any);
            }
        } else {
            const new_message = this.createMessage(message as IChatMessage);

            // The answered question should already exist in _conversation
            if (!new_message.answer) {
                this._conversation.next([...this._conversation.value, new_message]);
            }

            this._socket.next({ ...new_message, meeting_id } as any);
        }

        this.save();
    }

    /**
     * Add message to local message logs
     * @param message IChatMessage
     */
    public add(message: Partial<IChatMessage>): IChatMessage {
        if (!message) return;

        const new_message = this.createMessage(message);
        const conversation = [...this._conversation.value, new_message];

        this._conversation.next(conversation);

        return new_message;
    }

    /**
     * Store messages
     * @param message IChatMessage
     */
    public store(message: Partial<IChatMessage>): void {
        if (!message) return;
        if (this._participants.getValue().length <= 2) {
            this._users.postNewChat();
        }
        const new_message = this.createMessage(message);
        const conversation = [...this._conversation.value, new_message];

        // The answered question should already exist in _conversation
        if (!new_message.answer) {
            this._conversation.next(conversation);
        }

        if (this._meeting_id) {
            const messages_stored = [...this.getSession(this.messages_stored), new_message];
            this.setSession(this.messages_stored, messages_stored);
        }
    }

    /**
     * Send and clear stored messages
     */
    public sendStoredMessages(): void {
        if (!this._socket) return;

        this.getSession(this.messages_stored).forEach((message) => {
            this._socket.next({ ...message, meeting_id: this._meeting_id } as any);
        });

        this.removeSession(this.messages_stored);
    }

    /**
     * Session key for stored messsages
     */
    private get messages_stored(): string {
        return 'MVC_NSW_HP.messages.stored';
    }

    /**
     * Session key for conversation messsages
     */
    private get messages_meeting(): string {
        return `MVC_NSW_HP.messages.${this._meeting_id}`;
    }

    /**
     * Return array of message form session storage
     */
    private getSession(key: string): IChatMessage[] {
        if (!sessionStorage) return;
        return JSON.parse(sessionStorage.getItem(key)) || [];
    }

    /**
     * Set array of message to session storage
     */
    private setSession(key: string, messages: IChatMessage[]): void {
        if (!sessionStorage) return;
        sessionStorage.setItem(key, JSON.stringify(messages));
    }

    /**
     * Remove session storage
     */
    private removeSession(key: string): void {
        if (!sessionStorage) return;
        sessionStorage.removeItem(key);
    }

    /** Create new message */
    private createMessage(message: Partial<IChatMessage>): IChatMessage {
        const timestamp = dayjs().valueOf();
        let user = null;

        if (message.type === 'message') {
            user = this._users.current;
        } else if (message.type === 'chatbot') {
            user = this.chat_bot;
        }

        return {
            id: `${timestamp}`,
            timestamp,
            user,
            text: message.text,
            answers: message.answers,
            answer: message.answer,
            type: message.type || 'message',
            style: message.style || 'text',
            display_time: '',
            active_user: true,
            role: this._users.current.type,
        };
    }

    /**
     * Temporarily store conversation messages
     */
    private save(): void {
        if (!this._meeting_id) return;

        // Exclude files from session storage
        const messages = this._conversation.value.filter((message) => {
            !message.file && message.style !== 'file';
        });

        this.setSession(this.messages_meeting, messages);
    }

    /**
     * Load temp conversation messages
     */
    private load(): void {
        if (!this._meeting_id) return;

        // All saved conversion messages
        const conversation = this.getSession(this.messages_meeting);

        // Merge stored messages ensuring no duplicates
        this.getSession(this.messages_meeting).forEach((message) => {
            const exists = conversation.find(({ text }) => message.text === text);

            if (!exists) {
                conversation.push(message);
            }
        });

        // Ensure oldest to newest
        const sorted_conversation = conversation.sort((a, b) => a.timestamp - b.timestamp);

        this._conversation.next(sorted_conversation);
    }

    /**
     * Format new message from socket
     */
    private handleMessage(message: IChatResponse): void {
        const conversation = [...this._conversation.value];
        conversation.push({
            id: `${message.time}`,
            timestamp: message.time,
            text: message.text,
            type: 'message',
            style: 'text',
            user: new User(this._users, {
                id: message.from || message.name,
                name: message.name,
                type: message.role,
            }),
        });
        this.playSound();
        this._conversation.next(conversation);
    }

    /**
     * Handle file message
     * @param message Message to handle
     */
    private handleFile(message: IChatResponse): void {
        const conversation = [...this._conversation.value];
        if (!this._files[message.file_name]) {
            this._files[message.file_name] = [];
            const parts = message.file_name.split('.');
            conversation.push({
                id: `${message.time}`,
                timestamp: message.time,
                text: message.file_name,
                type: 'message',
                style: 'file',
                file: null,
                complete: '0',
                file_type: parts[parts.length - 1],
                user: new User(this._users, {
                    id: message.from || message.name,
                    name: message.name,
                    type: message.role,
                }),
                role: message.role,
                active_user: message.from === this._users.current.id,
            });
            this._events.next({ type: 'file', message });
        }
        const file = this._files[message.file_name].find(_ => _.file_part === message.file_part);
        if (!file && message.text) this._files[message.file_name].push(message);
        else if (file) file.completed = true
        const msg = findLast(conversation, (i) => i.text === message.file_name);
        msg.complete = (
            (this._files[message.file_name].filter(_ => _.completed).length / message.total_parts) *
            100
        ).toFixed(1);
        if (this._files[message.file_name].length >= message.total_parts) {
            const file = this.assembleFile(message.file_name);
            msg.file = file;
            delete this._files[message.file_name];
            this.playSound();
        }
        this._conversation.next(conversation);
    }

    /**
     * Assemble file chunks into the file's data URI
     * @param file_name Name of the file to assemble
     */
    private assembleFile(file_name: string): string {
        const chunks = this._files[file_name];
        if (chunks.length === chunks[0].total_parts) {
            // all file parts are here
            const dataURI = chunks
                .sort((a, b) => a.file_part - b.file_part)
                .map((el) => el.text)
                .join('');
            return dataURI;
        }
        return '';
    }

    /** Update display times for conversation messages */
    private updateDisplayTimes() {
        const conversation = [...this._conversation.value];

        conversation.forEach((msg, i) => {
            msg.display_time = formatChatMessageTime(msg.timestamp);
            msg.consecutive =
                i !== 0 &&
                msg.type !== 'notice' &&
                msg.user &&
                conversation[i - 1].user &&
                msg.user.id === conversation[i - 1].user.id;
        });

        this._conversation.next(conversation);
    }

    /**
     * Ping the websocket to keep it alive
     */
    private ping() {
        if (this._socket) {
            this._socket.next({ text: '', meeting_id: 'ping' } as any);
        }
    }

    /** Play sound */
    private playSound() {
        const audio = new Audio('assets/chat.wav');
        audio.play();
    }

    /** Loop through chatbot system messages and notify observer */
    private chatbotSystem(space: Space, messages: IChatMessage[]): void {
        if (messages.length === 0) return;

        const message = messages.shift();

        if (!this._is_alone && message.key === 'wait-time-notice') return;

        this.timeout(
            'chatbot.system',
            () => {
                this.add(message);
                this.chatbotSystem(space, messages);
            },
            message.delay
        );
    }

    /** Loop through chatbot standard messages and notify observer */
    private chatbotStandard(space: Space, messages: IChatMessage[], message?: IChatMessage): void {
        if (!this._is_alone || !messages?.length) return;

        let delay: number;
        let new_message: IChatMessage;

        const is_question = !!message?.answers;
        const is_answered = !!message?.answer;

        if (is_question && !is_answered) {
            delay = 1000;
        } else {
            new_message = messages.shift();
            delay = new_message.delay;
        }

        if (new_message?.key === 'intro-message') {
            new_message.text = new_message.text.replace(
                '{{ patient_name }}',
                this._users.current.name
            );
        }

        this.timeout(
            'chatbot.standard',
            () => {
                if (new_message) {
                    message = this.add(new_message);
                }

                this.chatbotStandard(space, messages, message);
            },
            delay
        );
    }
}
